import type { SortType } from 'rsuite';
import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../../components/Dropdown/Dropdown';
import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';
import type { FileButtonUploadFn } from '../../../components/FileButton/FileButton';
import type { PageChangeFn } from '../../../components/Pagination/Pagination';
import type { ResultsPerPageChangeFn } from '../../../components/Pagination/ResultsPerPageDropdown';
import type {
  Media,
  MediaHydrated,
  RelationWithoutHydration,
} from '../../../lib/flotiq-client/types/api-response';
import type { NewMediaObject } from '../../../lib/store/newMediaStore';

import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';

import {
  batchDeleteMedia,
  batchUpdateMedia,
  updateMedia,
} from '../../../lib/flotiq-client';
import {
  getTagsFromQuery,
  patchMedia,
} from '../../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../../lib/flotiq-client/response-errors';
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../lib/globals';
import { getTagUrl, getTestProps } from '../../../lib/helpers';
import {
  findBorderColor,
  findThumbnail,
  getMediaData,
} from '../../../lib/newMediaHelpers';

import Button from '../../../components/Button/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Dropdown from '../../../components/Dropdown/Dropdown';
import FileButton from '../../../components/FileButton/FileButton';
import Heading from '../../../components/Heading/Heading';
import Input from '../../../components/Input/Input';
import MediaCard from '../../../components/MediaCard/MediaCard';
import MediaDataGrid from '../../../components/MediaDataGrid/MediaDataGrid';
import MediaGrid from '../../../components/MediaGrid/MediaGrid';

import { useContentObjects, useMediaTags } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';
import useMediaUpload from '../../../hooks/api/useMediaUpload';
import useDebounceCallback from '../../../hooks/useDebounceCallback';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import { useModals } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';

import { ArrowUpIcon } from '../../../images/shapes';

import VariantModalForm from '../../../form/VariantModalForm/VariantModalForm';
import TagsManagementModal from '../TagsManagementModal/TagsManagementModal';
import { getExtensionOptions, parseExtensionFilters } from './extenstions';

interface UserMediaProps {
  /**
   * Limit of an element on page
   */
  mediaOnPageLimit?: number;
  /**
   * Set the limit of an element on page
   */
  setMediaOnPageLimit?: ResultsPerPageChangeFn;
  /**
   * On upload callback
   */
  onUpload?: FileButtonUploadFn;
  /**
   * If file selection is multiple
   */
  multipleUpload?: boolean;
  /**
   * If media card is editable
   */
  inModal?: boolean;
  /**
   * Callback for selecting media
   */
  selectMedia?: (media: Media | MediaHydrated, type?: string) => void;
  /**
   * Selected media
   */
  selectedMedia?: Record<string, any>;
  /**
   * Callback for clearing all media selections
   */
  clearSelection?: () => void;
  /**
   * If media filters are in panel
   */
  hasPanel?: boolean;
  /**
   * If grid view is allowed to change
   */
  hasGridView?: boolean;
  /**
   *   CSS classes for Media grid container
   */
  additionalGridContainerClasses?: string;
  /**
   *  Callback on remove media
   */
  onRemove?: () => void;
  /**
   * Default filters
   */
  defaultFilters?: Record<string, { filter: string | string[]; type: string }>;
  /**
   *   Test id for user media subpage
   */
  testId?: string;
}

const UserMedia = ({
  mediaOnPageLimit = 8,
  setMediaOnPageLimit = EMPTY_FUNCTION,
  onUpload,
  onRemove = EMPTY_FUNCTION,
  multipleUpload = true,
  inModal = false,
  selectMedia = EMPTY_FUNCTION,
  selectedMedia = EMPTY_OBJECT,
  clearSelection = EMPTY_FUNCTION,
  hasPanel = true,
  hasGridView = false,
  defaultFilters,
  additionalGridContainerClasses = '',
  testId = '',
}: UserMediaProps) => {
  const { t } = useTranslation();
  const { newMedia, removeNewMedia } = useMediaUpload();
  const jwt = useToken();
  const { space, buildUrlWithSpace } = useSelectedSpace();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilters || {});
  const [order, setOrder] = useState({
    order_by: 'internal.createdAt',
    order_direction: 'desc' as SortType,
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    {},
  );
  const [managementErrors, setManagementErrors] = useState<
    Record<string, string | string[]>
  >({});
  const [tagsData, setTagsData] = useState<DropdownOption[]>([]);

  const { permissions } = use(UserContext);

  const { canDelete, canUpdate } = useMemo(
    () => permissions.getCoPermissions('_media') || {},
    [permissions],
  );
  const canReadTags = permissions.canCo('_tag');
  const canManageTags = canUpdate && canReadTags;

  const handlePageChange = useCallback<PageChangeFn>((pageNo) => {
    setPage(pageNo);
    setManagementErrors({});
  }, []);

  const modal = useModals();

  const handleFilters = useCallback(
    (
      name: string,
      value: string | string[] | null,
      type: string = 'contains',
    ) => {
      setPage(1);
      setFilters((prev) => {
        const newFilters = { ...prev };
        if (!value) delete newFilters[name];
        else
          newFilters[name] = {
            filter: value,
            type: type,
          };
        return newFilters;
      });
      setManagementErrors({});
    },
    [],
  );

  const debouncedHandleSearch = useDebounceCallback(
    (value) => handleFilters('fileName', value),
    300,
  );

  const handleOrder = useCallback((name: string, value?: string) => {
    setOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
    setManagementErrors({});
  }, []);

  const tagsParams = useMemo(() => {
    return {
      page: 1,
      limit: 20,
      order_by: 'name',
    };
  }, []);

  const { data: tagData, isLoading: isLoadingTag } = useMediaTags(tagsParams);

  const sortOptions = useMemo(() => {
    return [
      { value: 'fileName', label: t('Media.Name') },
      { value: 'extension', label: t('Media.Extension') },
      { value: 'size', label: t('Media.Size') },
      {
        value: 'internal.createdAt',
        label: t('Media.UploadDate'),
      },
      { value: 'type', label: t('Media.Type') },
    ];
  }, [t]);

  useEffect(() => {
    setTagsData(
      tagData.map((element) => ({
        value: getTagUrl(element.id),
        label: element.name,
      })),
    );
  }, [tagData]);

  const orderOptions = useMemo(() => {
    return [
      {
        value: 'asc',
        searchString: t('Media.Filters.OrderOptions.Asc'),
        label: (
          <div className="h-full flex items-center">
            <ArrowUpIcon className={'mr-2 h-3 text-blue'} />
            {t('Media.Filters.OrderOptions.Asc')}
          </div>
        ),
      },
      {
        value: 'desc',
        searchString: t('Media.Filters.OrderOptions.Desc'),
        label: (
          <div className="h-full flex items-center">
            <ArrowUpIcon className={'mr-2 h-3 text-blue rotate-180'} />
            {t('Media.Filters.OrderOptions.Desc')}
          </div>
        ),
      },
    ];
  }, [t]);

  const extensionOptions = useMemo(
    () => getExtensionOptions((filters.extension?.filter || []) as string[], t),
    [filters.extension?.filter, t],
  );

  const customFilters = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      setIsLoading(true);
      const newOptions = await getTagsFromQuery(
        jwt,
        space,
        query,
        t,
        tagsParams,
      );
      setIsLoading(false);
      return newOptions.map((element) => ({
        value: getTagUrl(element.id),
        label: element.name,
      }));
    },
    [t, space, tagsParams, jwt],
  );

  const handleActiveFilters = useCallback(
    (key: string, value?: string | null) => {
      setActiveFilters((prevState) => {
        const newFilters = { ...prevState };
        if (value) newFilters[key] = value;
        else delete newFilters[key];
        return newFilters;
      });
    },
    [],
  );

  const onExtensionFilterChange = useCallback<DropdownChangeFn>(
    (event) => {
      const newValueWithoutDuplications = parseExtensionFilters(
        event.target.value || [],
      );

      handleFilters(
        'extension',
        newValueWithoutDuplications.length ? newValueWithoutDuplications : null,
        'equals',
      );
      handleActiveFilters(
        'extension',
        (newValueWithoutDuplications || [])
          .map((extension) => extension)
          .join(', '),
      );
    },
    [handleActiveFilters, handleFilters],
  );

  const filtersNode = useMemo(() => {
    return (
      <div
        className={
          'md:grid-flow-dense md:grid md:gap-6 md:grid-cols-3 xl:grid-cols-5 '
        }
      >
        <Input
          label={t('Media.Filters.SearchByName')}
          placeholder={t('Media.Filters.SearchByName')}
          onChange={(event) => {
            const value = event.target.value;
            if (!value) handleFilters('fileName', value);
            else debouncedHandleSearch(value);
            handleActiveFilters('fileName', value);
          }}
          value={filters?.fileName?.filter}
          type="search"
          {...getTestProps(testId, 'filter-name', 'testId')}
        />
        <Dropdown
          label={t('Media.Filters.SearchByTag')}
          placeholder={t('Media.Filters.SearchByTag')}
          options={tagsData}
          isDataLoading={isLoadingTag}
          onChange={(event) => {
            handleFilters('tags[*].dataUrl', event.target.value, 'contains');
            handleActiveFilters('tag', event.option?.label as string);
          }}
          filterCallback={customFilters}
          emptyOptions={<div className="py-2 px-4">{t('Media.EmptyTags')}</div>}
          value={filters?.['tags[*].dataUrl']?.filter}
          additionalOptionsClasses="py-1.5"
          debounceTime={500}
          nullable
          ignoreNotFound
          {...getTestProps(testId, 'filter-tag', 'testId')}
        />
        <Dropdown
          label={t('Media.Filters.SearchByExtension')}
          placeholder={t('Media.Filters.SearchByExtension')}
          options={extensionOptions}
          onChange={onExtensionFilterChange}
          value={filters?.extension?.filter}
          additionalOptionsClasses="py-1.5"
          additionalDropdownClasses="max-h-60"
          nullable
          multiple
          maxVisibleValues={1}
          maxVisibleValuesText="..."
          {...getTestProps(testId, 'filter-extension', 'testId')}
        />
        <Dropdown
          label={t('Media.Filters.SortBy')}
          placeholder={t('Media.Filters.SortBy')}
          options={sortOptions}
          onChange={(event) => {
            handleOrder('order_by', event.target.value);
          }}
          value={order?.order_by}
          additionalOptionsClasses="py-1.5"
          additionalDropdownClasses="max-h-44"
          nullable
          {...getTestProps(testId, 'order-by', 'testId')}
        />
        <div className={'w-full xl:w-2/3'}>
          <Dropdown
            testId={`${testId}-Order`}
            label={t('Media.Filters.Order')}
            placeholder={t('Media.Filters.Order')}
            options={orderOptions}
            onChange={(event) => {
              handleOrder('order_direction', event.target.value);
            }}
            value={order?.order_direction}
            additionalOptionsClasses="py-1.5"
            nullable
            {...getTestProps(testId, 'order', 'testId')}
          />
        </div>
      </div>
    );
  }, [
    t,
    filters,
    testId,
    tagsData,
    isLoadingTag,
    customFilters,
    extensionOptions,
    onExtensionFilterChange,
    sortOptions,
    order?.order_by,
    order?.order_direction,
    orderOptions,
    handleFilters,
    debouncedHandleSearch,
    handleActiveFilters,
    handleOrder,
  ]);

  const params = useMemo(() => {
    return {
      limit: mediaOnPageLimit,
      page: page,
      filters: JSON.stringify(filters),
      hydrate: 1,
      ...order,
    };
  }, [mediaOnPageLimit, page, filters, order]);

  const { data, errors, isLoading, pagination, reload } = useContentObjects(
    '_media',
    params,
  );

  const reloadMedia = useCallback(() => {
    reload();
    removeNewMedia();
  }, [reload, removeNewMedia]);

  useEffect(() => {
    if (pagination?.total_pages < page) setPage(pagination.total_pages);
  }, [page, pagination?.total_pages]);

  useApiErrorsToast(errors);

  useEffect(() => {
    removeNewMedia();
  }, [isLoading, removeNewMedia]);

  const handleNoData = useMemo(
    () => (
      <div
        className={twMerge(
          'md:text-5xl md:leading-[52px] font-bold text-2xl',
          'flex flex-col items-center py-2 lg:py-8 text-center',
        )}
        {...getTestProps(testId, 'drop-files')}
      >
        <p className="text-blue-600">{t('Media.DropFiles')}</p>
        <p className="text-slate-900 dark:text-slate-200 m-0 mb-6">
          {t('Media.AddToLibrary')}
        </p>
        <FileButton
          onUpload={onUpload}
          multiple={multipleUpload}
          additionalClasses="hidden md:flex"
          {...getTestProps(testId, 'file', 'testId')}
        />
      </div>
    ),
    [t, onUpload, multipleUpload, testId],
  );

  const selectAllMediaInPage = useCallback(
    (checked: boolean) => {
      if (checked) {
        data.forEach((mediaData) => {
          selectMedia(mediaData as MediaHydrated, '_media');
        });
        newMedia.forEach(({ media, state }) => {
          if (state === 'uploaded') {
            selectMedia(media, '_media');
          }
        });
      } else {
        clearSelection();
      }
    },
    [data, newMedia, selectMedia, clearSelection],
  );

  const applyTagFilter = useCallback(
    (tagId: string, tagName: string) => {
      const tagUrl = getTagUrl(tagId);
      const selectedTagFilter = {
        value: tagUrl,
        label: tagName,
      };
      if (
        tagsData.filter(
          (element) =>
            element.value === selectedTagFilter.value &&
            element.label === selectedTagFilter.label,
        ).length === 0
      ) {
        setTagsData((prev) => prev.concat([selectedTagFilter]));
      }
      handleFilters('tags[*].dataUrl', tagUrl, '_contains');
      handleActiveFilters('tag', selectedTagFilter.label);
    },
    [handleActiveFilters, handleFilters, tagsData],
  );

  const handleTagUpdate = useCallback(
    async (
      media: (Media | MediaHydrated)[],
      tags: RelationWithoutHydration[],
    ) => {
      try {
        let updateResult;
        if (media.length === 1) {
          updateResult = await updateMedia(jwt, space, {
            mediaId: media[0].id,
            tags: tags,
          });
        } else {
          updateResult = await batchUpdateMedia(
            jwt,
            space,
            media.map((element) => {
              const elementTags = (element.tags || []).filter(
                (tag) =>
                  tags.findIndex(
                    (newTag) =>
                      ('id' in tag && newTag.dataUrl === getTagUrl(tag.id)) ||
                      tag.dataUrl === newTag.dataUrl,
                  ) < 0,
              );
              return {
                id: element.id,
                tags: tags.concat(
                  elementTags.map((element) => ({
                    dataUrl:
                      'id' in element ? getTagUrl(element.id) : element.dataUrl,
                    type: 'internal',
                  })),
                ),
              };
            }),
          );
        }
        if (!updateResult.ok) {
          throw parseResponseErrors(updateResult.body, updateResult.status);
        }
        toast.success(t('TagsManagement.TagEditSuccess'));
        reloadMedia();
        clearSelection();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(error.message);
        }
      }
    },
    [jwt, space, reloadMedia, clearSelection, t],
  );

  const handleTagManagementModal = useCallback(
    async (media: Media | MediaHydrated | (Media | MediaHydrated)[]) => {
      const mediaData = Array.isArray(media) ? media : [media];
      setManagementErrors({});
      await modal({
        title: (
          <Heading level={4} additionalClasses="truncate dark:text-slate-200">
            {mediaData.length === 1
              ? t('TagsManagement.AddTagsToAsset', {
                  name: mediaData[0]?.fileName,
                })
              : t('TagsManagement.AddTagsToAssets', {
                  counter: mediaData.length,
                })}
          </Heading>
        ),
        content: (
          <TagsManagementModal
            media={mediaData}
            handleTagUpdate={handleTagUpdate}
            {...getTestProps(testId, 'tags-management-modal', 'testId')}
          />
        ),
        dialogAdditionalClasses: 'overflow-visible!',
      });
    },
    [modal, t, testId, handleTagUpdate],
  );

  const handleDeleteMedia = useCallback(
    async (
      selectedMediaToDelete:
        Record<string, Media | MediaHydrated> | (Media | MediaHydrated)[],
    ) => {
      setManagementErrors({});

      try {
        const { body, status, ok } = await batchDeleteMedia(
          jwt,
          space,
          Object.values(selectedMediaToDelete).map((element) => element.id),
        );
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Media.MediaManagement.DeleteSuccess'));
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(
            error.message
              ? error.message
              : t('Media.MediaManagement.CouldntDelete'),
          );
          if (error.errors) {
            setManagementErrors((prev) => {
              const errors = { ...prev };

              (Array.isArray(error.errors)
                ? error.errors
                : [error.errors]
              ).forEach((error) => {
                const objectIdRegex = /_media\/(?<id>[^\s]*)/;
                const regexData = error.match(objectIdRegex);
                if (regexData?.groups) {
                  const { id } = regexData.groups;
                  errors[id] = error;
                }
              });
              return errors;
            });
          }
        }
      }

      clearSelection();
      reloadMedia();
      onRemove?.();
    },
    [clearSelection, reloadMedia, jwt, space, t, onRemove],
  );

  const showDeletingModal = useCallback(
    async (
      selectedMediaToDelete:
        Record<string, Media | MediaHydrated> | (Media | MediaHydrated)[],
    ) => {
      modal.deleting('delete-media-modal', {
        testId: `${testId}-delete-confirmation-modal`,
      });
      await handleDeleteMedia(selectedMediaToDelete);
    },
    [handleDeleteMedia, modal, testId],
  );

  const deleteMedia = useCallback(
    async (
      selectedMediaToDelete:
        Record<string, Media | MediaHydrated> | (Media | MediaHydrated)[],
    ) => {
      const count = Object.keys(selectedMediaToDelete).length;
      await modal.delete(
        <div className="dark:text-slate-200">
          <Heading additionalClasses="dark:text-slate-200" level={6}>
            {t('Media.MediaManagement.DeleteConfirmation', { count })}
          </Heading>
          <p>{t('Media.MediaManagement.DeleteWarning')}</p>
        </div>,
        'delete-media-modal',
        () => showDeletingModal(selectedMediaToDelete),
        () => null,
        {
          testId: `${testId}-delete-confirmation-modal`,
        },
      );
    },
    [modal, showDeletingModal, t, testId],
  );

  const selectedMediaActionBar = useMemo(() => {
    if (inModal) return;

    const selectedMediaCount = selectedMedia
      ? Object.values(selectedMedia).length
      : 0;
    return (
      <div className="flex flex-col lg:flex-row gap-1 lg:gap-2 w-full">
        <div className="flex flex-row items-center h-10 justify-start w-full md:w-fit">
          {(canManageTags || canDelete) && (
            <Checkbox
              name="select"
              additionalCheckboxClasses="w-6 h-6"
              onChange={(event) => selectAllMediaInPage(event.target.checked)}
              circular
              checked={
                selectedMedia && Object.values(selectedMedia).length !== 0
              }
              label={
                selectedMediaCount > 0
                  ? t('Media.MediaManagement.AssetSelectedCounter', {
                      counter: selectedMediaCount,
                    })
                  : t('Media.MediaManagement.SelectAll')
              }
              additionalLabelClasses="text-sm md:text-base mr-2"
              {...getTestProps(testId, 'select-all-media', 'testId')}
            />
          )}
        </div>
        {selectedMediaCount > 0 && (canManageTags || canDelete) && (
          <div className="flex flex-wrap gap-1 lg:gap-2 justify-start w-full md:w-fit">
            {canManageTags && (
              <Button
                buttonColor="blueBordered"
                onClick={() =>
                  handleTagManagementModal(Object.values(selectedMedia))
                }
              >
                {t('Media.MediaManagement.AddTags')}
              </Button>
            )}
            {canDelete && (
              <Button
                buttonColor="redBordered"
                onClick={() => deleteMedia(selectedMedia)}
              >
                {t('Media.MediaManagement.DeleteSelected')}
              </Button>
            )}
            <Button buttonColor="blueBordered" onClick={() => clearSelection()}>
              {t('Media.MediaManagement.CancelSelect')}
            </Button>
          </div>
        )}
      </div>
    );
  }, [
    inModal,
    selectedMedia,
    canManageTags,
    canDelete,
    t,
    testId,
    selectAllMediaInPage,
    handleTagManagementModal,
    deleteMedia,
    clearSelection,
  ]);

  const onMediaClick = useCallback(
    (mediaData: MediaHydrated) => {
      selectMedia(mediaData, '_media');
    },
    [selectMedia],
  );

  const onCardDelete = useCallback(
    (media: MediaHydrated) => deleteMedia([media]),
    [deleteMedia],
  );

  const openVariantModal = useCallback(
    async (media: MediaHydrated) => {
      const newVariant = await modal({
        title: t('MediaEdit.AddVariant'),
        content: (
          <VariantModalForm
            media={media}
            variantsNames={(media.variants || []).map(
              (variant) => variant.name,
            )}
          />
        ),
        size: 'xl',
        dialogAdditionalClasses: 'max-h-screen',
      });

      if (newVariant) {
        const [, hasErrors] = await patchMedia(
          jwt,
          space,
          media.id,
          {
            variants: [...(media.variants || []), newVariant],
          },
          t,
          t('MediaEdit.Variant.Updated'),
          t('MediaEdit.Variant.UpdateError'),
        );

        if (!hasErrors) reloadMedia();
      }
    },
    [jwt, modal, reloadMedia, t, space],
  );

  const navigateToEdit = useCallback(
    (media: MediaHydrated) => {
      navigate(buildUrlWithSpace(`media/edit/${media.id}`));
    },
    [navigate, buildUrlWithSpace],
  );

  const renderMedia = useCallback(
    (media: MediaHydrated | NewMediaObject, isNew = false) => {
      const {
        card,
        error,
        isLoaded = true,
      } = isNew
        ? getMediaData(media as NewMediaObject)
        : {
            card: media as MediaHydrated,
            error: managementErrors?.[(media as MediaHydrated).id],
          };

      return (
        <MediaCard
          key={card.id}
          mediaData={card as MediaHydrated}
          thumbnail={findThumbnail(error, isLoaded, false, testId)}
          hideOverlay={!!(error || !isLoaded)}
          onClick={
            isLoaded && !error && (canManageTags || canDelete)
              ? onMediaClick
              : undefined
          }
          isSelected={!!selectedMedia?.[card.id]}
          onDelete={onCardDelete}
          onEdit={inModal ? undefined : navigateToEdit}
          onVariantCreate={error || !isLoaded ? undefined : openVariantModal}
          onTagEdit={canManageTags ? handleTagManagementModal : undefined}
          onTagClick={applyTagFilter}
          additionalContainerClasses={findBorderColor(error, isLoaded, isNew)}
          additionalThumbnailClasses={
            managementErrors?.[card.id] &&
            'absolute inset-0 bg-white opacity-70'
          }
          activeSortOrder={order?.order_by}
          {...getTestProps(testId, `media-card-${card.id}`, 'testId')}
        />
      );
    },
    [
      managementErrors,
      testId,
      canManageTags,
      canDelete,
      onMediaClick,
      selectedMedia,
      onCardDelete,
      inModal,
      navigateToEdit,
      openVariantModal,
      handleTagManagementModal,
      applyTagFilter,
      order?.order_by,
    ],
  );

  return (
    <MediaGrid
      page={page}
      data={data}
      renderMedia={renderMedia}
      isLoading={isLoading}
      errors={errors}
      pagination={pagination}
      handlePageChange={handlePageChange}
      areFiltersApplied={Object.keys(filters).length !== 0}
      filtersNode={filtersNode}
      handleNoData={handleNoData}
      selectedMediaActionBarNode={selectedMediaActionBar}
      newMedia={newMedia}
      selectedMedia={selectedMedia}
      hasPanel={hasPanel}
      emptyFilterText={t('Media.FiltersEmptyResult')}
      activeFilters={activeFilters}
      resultsPerPage={mediaOnPageLimit}
      setResultsPerPage={setMediaOnPageLimit}
      gridView={
        hasGridView ? (
          <MediaDataGrid
            media={data as MediaHydrated[]}
            isLoading={isLoading}
            pagination={pagination}
            limit={mediaOnPageLimit}
            reloadMedia={reload}
            order={order}
            handleOrder={handleOrder}
            selectedMedia={selectedMedia}
            selectMedia={selectMedia}
            onTagEdit={canManageTags ? handleTagManagementModal : undefined}
            onTagClick={applyTagFilter}
            onVariantCreate={openVariantModal}
            testId={testId}
          />
        ) : null
      }
      additionalGridContainerClasses={additionalGridContainerClasses}
      testId={testId}
    />
  );
};

export default UserMedia;
